import React, { useState, useRef, useEffect } from 'react';
import { Upload, ChevronDown, FileText, ArrowRight, Loader2 } from 'lucide-react';
import FileDropZone from './FileDropZone';

interface UploadSectionProps {
  file: File | null;
  isDragOver: boolean;
  isUploading: boolean;
  uploadStatus: string;
  selectedSheet: string;
  uploadProgress?: number;
  processingProgress?: number;
  processingStatusText?: string;
  isProcessing?: boolean;
  isParsing?: boolean;
  hasParsedData?: boolean;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onUpload: (e: React.FormEvent<HTMLFormElement>) => void;
  onParseExcel?: () => void;
  onNavigateToEntry?: () => void;
  onSheetChange: (sheet: string) => void;
}

const UploadSection: React.FC<UploadSectionProps> = ({
  file,
  isDragOver,
  isUploading,
  uploadStatus,
  selectedSheet,
  uploadProgress = 0,
  processingProgress = 0,
  processingStatusText = "",
  isProcessing = false,
  isParsing = false,
  hasParsedData = false,
  onDrop,
  onDragOver,
  onDragLeave,
  onFileChange,
  onUpload,
  onParseExcel,
  onNavigateToEntry,
  onSheetChange
}) => {
  const sheetOptions = [
    // Generation Reports Sheets
    { value: "master", label: "Daily Report", category: "Generation Reports" },
    { value: "databank", label: "Inverters", category: "Generation Reports" },
    { value: "bd", label: "Breakdowns", category: "Generation Reports" },
    { value: "ambient", label: "Weather", category: "Generation Reports" },
    { value: "meter", label: "Plant Meters", category: "Generation Reports" },
    // O&M(Checklist) Sheets
    { value: "module_cleaning", label: "Module Cleaning", category: "O&M(Checklist)" },
    { value: "grass_cutting", label: "Grass Cutting", category: "O&M(Checklist)" },
    { value: "daily_checklist", label: "Daily Checklist", category: "O&M(Checklist)" },
    { value: "weekly_checklist", label: "Weekly Checklist", category: "O&M(Checklist)" },
    { value: "monthly_checklist", label: "Monthly Checklist", category: "O&M(Checklist)" },
    { value: "quarterly_checklist", label: "Quarterly Checklist", category: "O&M(Checklist)" },
    { value: "yearly_checklist", label: "Yearly Checklist", category: "O&M(Checklist)" }
  ];

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isDropdownOpen]);

  const selectedOption = sheetOptions.find(opt => opt.value === selectedSheet);
  const displayValue = selectedOption ? selectedOption.label : "-- Select File --";

  const handleSelect = (value: string) => {
    onSheetChange(value);
    setIsDropdownOpen(false);
  };

  // Determine current active progress metrics
  const isUploadSuccess = uploadStatus.startsWith("success");
  const isUploadError = uploadStatus.startsWith("error");
  const showProgressBar = Boolean(file || isUploading || isUploadSuccess);

  const activePercent = !isUploading 
    ? (isUploadSuccess ? 100 : 0)
    : isProcessing 
    ? Math.min(100, Math.max(5, processingProgress))
    : Math.min(100, uploadProgress);

  const activeLabel = !isUploading
    ? (isUploadSuccess 
        ? "Upload & database save complete!" 
        : file 
        ? `Ready to upload ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`
        : "Select a file to begin upload")
    : isProcessing
    ? (processingStatusText || "Processing and saving database entries...")
    : `Uploading file to server... (${uploadProgress}%)`;

  return (
    <form onSubmit={onUpload} className="flex flex-col h-full">
      <div className="flex-shrink-0">
        <FileDropZone
          file={file}
          isDragOver={isDragOver}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onFileChange={onFileChange}
        />
      </div>

      {/* Sheet Selection Dropdown */}
      <div className="flex-shrink-0 mt-4">
        <label className="block text-sm text-gray-700 mb-2" style={{ fontWeight: 400 }}>
          Select File Name
        </label>
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="w-full bg-white rounded-lg px-4 py-3 pr-10 text-sm font-medium text-gray-900 cursor-pointer flex items-center justify-between border-2 border-gray-300 hover:border-[#0084CF] transition-all duration-200 shadow-sm hover:shadow-md"
            style={{
              outline: 'none',
              outlineWidth: 0
            }}
          >
            <span className={selectedSheet ? "text-gray-900" : "text-gray-500"}>{displayValue}</span>
            <ChevronDown
              size={18}
              className={`text-gray-500 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {isDropdownOpen && (
            <div
              className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 max-h-80 overflow-y-auto"
              style={{
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
              }}
            >
              <button
                type="button"
                onClick={() => handleSelect("")}
                className={`w-full text-left px-3 py-2 text-sm font-normal hover:bg-gray-50 transition-colors ${selectedSheet === "" ? 'bg-gray-100' : ''}`}
                style={{ border: 'none', outline: 'none', fontWeight: 'normal', color: '#111827' }}
              >
                -- Select File --
              </button>
              {['Generation Reports', 'O&M(Checklist)'].map((category) => {
                const categoryOptions = sheetOptions.filter(opt => opt.category === category);
                if (categoryOptions.length === 0) return null;

                return (
                  <div key={category}>
                    <div className="px-3 py-1.5 bg-gray-100 text-xs font-semibold text-gray-600 uppercase tracking-wide sticky top-0">
                      {category}
                    </div>
                    {categoryOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleSelect(option.value)}
                        className={`w-full text-left px-4 py-2 text-sm font-normal hover:bg-gray-50 transition-colors ${selectedSheet === option.value ? 'bg-[#F2F8FC] text-[#1C4D88]' : ''}`}
                        style={{ border: 'none', outline: 'none', fontWeight: 'normal', color: selectedSheet === option.value ? '#1C4D88' : '#111827' }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Status Message */}
      {uploadStatus && (
        <div className={`flex-shrink-0 mt-4 p-3 rounded-lg text-xs font-medium ${uploadStatus.startsWith("success")
          ? "bg-[#89BC91] text-gray-900 border-2 border-[#1AAA07] shadow-sm"
          : "bg-red-50 text-red-800 border-2 border-[#A1343C] shadow-sm"
          }`}>
          {uploadStatus.split("|")[1] || uploadStatus}
        </div>
      )}

      {/* Action Buttons & Dynamic Progress Bar */}
      <div className="flex flex-col gap-3 flex-shrink-0 mt-4">
        {onParseExcel && (
          <button
            type="button"
            onClick={onParseExcel}
            disabled={!file || isParsing}
            className="flex items-center justify-center gap-1.5 bg-[#0084CF] hover:bg-[#006ba3] text-white px-4 py-2 rounded-lg font-semibold text-xs transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
          >
            {isParsing ? (
              <>
                <Loader2 className="animate-spin h-3.5 w-3.5 text-white" />
                <span>Parsing Excel...</span>
              </>
            ) : (
              <>
                <FileText size={14} />
                <span>Parse Excel & Map to Forms</span>
              </>
            )}
          </button>
        )}

        {onNavigateToEntry && hasParsedData && (
          <button
            type="button"
            onClick={onNavigateToEntry}
            className="flex items-center justify-center gap-1.5 bg-[#69951A] hover:bg-[#577a15] text-white px-4 py-2 rounded-lg font-semibold text-xs transition-all duration-200 shadow-md hover:shadow-lg"
          >
            <ArrowRight size={14} />
            <span>Go to Entry Page</span>
          </button>
        )}

        {/* Dynamic & Powerful Real-time Progress Bar */}
        {showProgressBar && (
          <div className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3.5 shadow-sm space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full border ${
                  isUploadSuccess
                    ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                    : isUploading && isProcessing
                    ? 'bg-[#E3F0F9] text-[#1C4D88] border-[#8AC4E6]'
                    : isUploading
                    ? 'bg-[#E3F0F9] text-[#1C4D88] border-[#8AC4E6]'
                    : 'bg-slate-200 text-slate-700 border-slate-300'
                }`}>
                  {isUploadSuccess
                    ? 'Completed'
                    : isUploading && isProcessing
                    ? 'Phase 2: Database Processing'
                    : isUploading
                    ? 'Phase 1: File Upload'
                    : 'Ready for Upload'}
                </span>
              </div>
              <span className="text-xs font-bold text-slate-800 tracking-tight">
                {activePercent}%
              </span>
            </div>

            {/* Progress Bar Track */}
            <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden relative p-0.5 shadow-inner">
              <div
                className={`h-full rounded-full transition-all duration-300 ease-out relative ${
                  activePercent === 100
                    ? 'bg-gradient-to-r from-emerald-500 to-green-600'
                    : isProcessing
                    ? 'bg-gradient-to-r from-[#0084CF] via-emerald-500 to-[#0066A3]'
                    : 'bg-gradient-to-r from-[#0084CF] to-[#0066A3]'
                }`}
                style={{ width: `${activePercent}%` }}
              >
                {isUploading && (
                  <div className="absolute inset-0 bg-white/25 w-full h-full animate-[shimmer_1.5s_infinite] origin-left" style={{
                    backgroundImage: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
                    backgroundSize: '50% 100%',
                    backgroundRepeat: 'no-repeat',
                  }}></div>
                )}
              </div>
            </div>

            {/* Dynamic Status Text from Backend */}
            <div className="flex items-center gap-1.5 text-xs font-medium text-slate-700">
              {isUploading ? (
                <Loader2 className="animate-spin h-3.5 w-3.5 text-[#0066A3] flex-shrink-0" />
              ) : isUploadSuccess ? (
                <span className="h-2 w-2 rounded-full bg-emerald-500 flex-shrink-0 inline-block"></span>
              ) : (
                <span className="h-2 w-2 rounded-full bg-slate-400 flex-shrink-0 inline-block"></span>
              )}
              <span className="truncate">{activeLabel}</span>
            </div>
          </div>
        )}

        {/* Upload Button */}
        <button
          type="submit"
          disabled={!file || isUploading}
          className={`flex items-center justify-center gap-2 px-5 py-3 rounded-lg font-semibold text-sm transition-all duration-200 shadow-md transform ${
            isUploading 
              ? 'bg-gradient-to-r from-[#0066A3] to-[#0066A3] text-white cursor-wait opacity-90'
              : 'bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-850 hover:to-slate-950 text-white hover:scale-[1.01] active:scale-[0.99]'
          } ${(!file || isUploading) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          {isUploading ? (
            <>
              <Loader2 className="animate-spin h-4 w-4 text-white" />
              <span>{isProcessing ? "Processing Data on Server..." : "Uploading File..."}</span>
            </>
          ) : (
            <>
              <Upload size={16} />
              <span>Upload to Server</span>
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default UploadSection;
